import React, { useEffect, useMemo, useState } from 'react'
import { Card, LinearProgress } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { PageWrapper } from '../../components/page-wrapper'
import { BreadcrumbItem } from '../../components/breadcrumbs'
import { OwnItemDetailsComponent } from './own-item-details-component'
import { useOwnItemDetailsPage } from './own-item-details-page.hooks'
import { useListedItemDetailsComponentStyles } from '../listed-item-details-page/listed-item-details-page.styles'
import { DraftEditor } from './draft-editor'
import { Alert } from '@material-ui/lab'
import { OWN_ITEM_LOADING_SHELL } from './own-item-loading-shell'
import { ItemState } from '../../models'

const idleWhileLoading: (state: ItemState) => undefined = () => undefined
const idleChangeMode: (mode: boolean) => undefined = () => undefined

export const OwnItemDetailsPage: React.FC = () => {
  const layoutClasses = useListedItemDetailsComponentStyles()
  const { t } = useTranslation()
  const [editMode, setEditMode] = useState<boolean>(false)
  const {
    detailProps,
    getOwnItemDetails,
    loading,
    deliveryType,
    setInStoreDeliveryType,
    draftData,
    setImages,
    submitItem,
    initialImages,
    ownItemState,
    categories,
    shippingAddressExists,
    isValidAddress,
    setCategories,
    selectedCategory,
    setSelectedCategory,
    nonprofits,
    setNonprofits,
    selectedNonprofit,
    setSelectedNonprofit,
    categoryRequest,
    nonprofitRequest,
    isDirtyDraft,
    updateState,
    setIsDirtyDraft,
  } = useOwnItemDetailsPage()

  useEffect(() => {
    getOwnItemDetails()
  }, [])

  const closeDraftMode = () => {
    setEditMode(false)
    setIsDirtyDraft(false)
  }

  const searchBreadcrumb = { label: t('header.menu.search'), to: '/search' as const }
  const listBreadcrumb = {
    label: t('header.menu.myListedItems'),
    to: '/my-listed-items' as const,
  }

  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    const baseBreadcrumbs = [searchBreadcrumb, listBreadcrumb]

    if (editMode) {
      return [...baseBreadcrumbs, { label: t('editItemDetailsPage.edit') }]
    }
    if (!detailProps) {
      if (!loading) return []
      return [...baseBreadcrumbs, { label: '…' }]
    }
    const itemLabel = detailProps.name?.trim() || (loading ? '…' : '—')
    return [...baseBreadcrumbs, { label: itemLabel }]
  }, [detailProps, loading, editMode, t])

  if (editMode) {
    return (
      <div className={layoutClasses.pageSurface}>
        {loading && <LinearProgress />}
        <PageWrapper
          title=""
          displayGoBackButton={true}
          goBackNavPath="/my-listed-items"
          breadcrumbItems={breadcrumbItems}
        >
          <Card elevation={0}>
            <DraftEditor
              {...draftData}
              editItemState={ownItemState}
              submitItem={submitItem}
              isDirtyDraft={isDirtyDraft}
              getSelectedData={() => {
                null
              }}
              categories={categories}
              shippingAddressExists={shippingAddressExists}
              isValidAddress={isValidAddress}
              setCategories={setCategories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              nonprofits={nonprofits}
              setNonprofits={setNonprofits}
              selectedNonprofit={selectedNonprofit}
              setSelectedNonprofit={setSelectedNonprofit}
              categoryRequest={categoryRequest}
              nonprofitRequest={nonprofitRequest}
              setImages={setImages}
              loading={loading}
              setIsDirtyDraft={setIsDirtyDraft}
              closeDraftMode={closeDraftMode}
              initialImages={initialImages}
            />
          </Card>
        </PageWrapper>
      </div>
    )
  }

  return (
    <div className={layoutClasses.pageSurface}>
      {loading && <LinearProgress />}
      <PageWrapper
        title=""
        displayGoBackButton={true}
        goBackNavPath="/my-listed-items"
        breadcrumbItems={breadcrumbItems}
      >
        <Card elevation={0}>
          <div className={layoutClasses.mainHolder}>
            {loading && !detailProps ? (
              <OwnItemDetailsComponent
                {...OWN_ITEM_LOADING_SHELL}
                editItemState={ownItemState}
                loading={true}
                deliveryType={deliveryType}
                updateState={idleWhileLoading}
                setInStoreDeliveryType={setInStoreDeliveryType}
                onChangeMode={idleChangeMode}
              />
            ) : detailProps && detailProps.isCurrentUserOwner ? (
              <OwnItemDetailsComponent
                {...detailProps}
                editItemState={ownItemState}
                loading={loading}
                deliveryType={deliveryType}
                updateState={updateState}
                setInStoreDeliveryType={setInStoreDeliveryType}
                onChangeMode={(mode: boolean) => setEditMode(mode)}
              />
            ) : (
              !loading && (
                <Alert severity="warning">This is not your item</Alert>
              )
            )}
          </div>
        </Card>
      </PageWrapper>
    </div>
  )
}
